import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMagneticHover } from "@/hooks/useMagneticHover";
import { appLoadingDone } from "@/App";
import CitySvg from "@/components/feature/CitySvg";

export default function HeroSection() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadPct, setLoadPct] = useState(0);
  const [phase, setPhase] = useState(0);
  const [titleVisible, setTitleVisible] = useState(false);
  const [buildingVisible, setBuildingVisible] = useState(false);
  const buildingRef = useRef<HTMLDivElement[]>([]);
  const scrollProgressRef = useRef(0);
  const cityRef = useRef<HTMLDivElement>(null);
  const hasRun = useRef(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const magneticRef1 = useMagneticHover(0.15);
  const magneticRef2 = useMagneticHover(0.15);

  // Loading + phase timeline — App のローディング完了を待つ
  useEffect(() => {
    const iv = setInterval(() => {
      setLoadPct(p => { if (p >= 100) { clearInterval(iv); return 100; } return p + 5; });
    }, 25);

    const waitForAppLoad = () => {
      if (appLoadingDone) {
        const t1 = setTimeout(() => setLoading(false), 100);
        const t2 = setTimeout(() => setPhase(1), 400);
        const t3 = setTimeout(() => setPhase(2), 600);
        const t4 = setTimeout(() => setPhase(3), 900);
        const t5 = setTimeout(() => setPhase(4), 1200);
        const t6 = setTimeout(() => setPhase(5), 1500);
        // ローディング完全フェードアウト後にビルアニメーション起動
        const t7 = setTimeout(() => {
          setBuildingVisible(true);
          const lines = document.querySelectorAll(".grid-line");
          lines.forEach((el, i) => {
            const line = el as HTMLElement;
            line.style.transition = "none";
            line.style.transform = "scaleY(0)";
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                line.style.transition = `transform 8s cubic-bezier(0.22,1,0.36,1) ${i * 0.2}s`;
                line.style.transform = "scaleY(1)";
              });
            });
          });
        }, 800);
        return () => [t1, t2, t3, t4, t5, t6, t7].forEach(clearTimeout);
      } else {
        const poll = setTimeout(waitForAppLoad, 50);
        return () => clearTimeout(poll);
      }
    };
    const cleanup = waitForAppLoad();

    return () => {
      clearInterval(iv);
      cleanup?.();
    };
  }, []);

  useEffect(() => {
    if (phase >= 3) {
      let raf1: number;
      let raf2: number;
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
          setTitleVisible(true);
        });
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    } else {
      setTitleVisible(false);
    }
  }, [phase]);

  useEffect(() => {
    const wrap = wrapRef.current;
    const sticky = wrap?.querySelector(".sticky-hero-viewport") as HTMLElement | null;
    if (!wrap || !sticky) return;

    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const rect = wrap.getBoundingClientRect();
        const scrollable = wrap.offsetHeight - window.innerHeight;
        const scrolled = -rect.top;
        const p = Math.max(0, Math.min(1, scrolled / scrollable));
        scrollProgressRef.current = p;

        const heroContent = sticky.querySelector(".hero-content-layer") as HTMLElement | null;
        const circleLayer = sticky.querySelector(".circle-reveal-layer") as HTMLElement | null;
        const curtain = sticky.querySelector(".bottom-curtain") as HTMLElement | null;
        const cityEl = cityRef.current;
        const gridLines = sticky.querySelectorAll(".grid-line");
        const rightLine = sticky.querySelector(".right-accent-line") as HTMLElement | null;
        const scrollIndicator = sticky.querySelector(".scroll-indicator") as HTMLElement | null;

        const heroFadeStart = 0.18;
        const heroFadeEnd = 0.55;
        const heroOpacity = p < heroFadeStart ? 1 : Math.max(0, 1 - (p - heroFadeStart) / (heroFadeEnd - heroFadeStart));
        const heroTranslateY = Math.max(0, p - heroFadeStart) * -50;
        if (heroContent) {
          heroContent.style.opacity = String(heroOpacity);
          heroContent.style.transform = `translateY(${heroTranslateY}px)`;
          heroContent.style.pointerEvents = heroOpacity < 0.05 ? "none" : "auto";
        }

        if (cityEl) {
          cityEl.style.opacity = String(Math.max(0, 1 - p * 2.2));
        }

        // gridLines の opacity はスクロールで制御するが transform は触らない
        gridLines.forEach((line) => {
          (line as HTMLElement).style.opacity = String(Math.max(0, 1 - p * 2.2));
        });

        if (rightLine) {
          rightLine.style.opacity = String(Math.max(0, 1 - p * 2.2));
        }

        const verticalText = sticky.querySelector(".hero-vertical-text") as HTMLElement | null;
        if (verticalText) {
          const vProgress = Math.max(0, Math.min(1, p * 25.0));
          verticalText.style.opacity = String(Math.max(0, 1 - vProgress));
          verticalText.style.transform = `translateY(${vProgress * -3500}px)`;
        }

        const horizontalText = sticky.querySelector(".hero-horizontal-text") as HTMLElement | null;
        if (horizontalText) {
          const hProgress = Math.max(0, Math.min(1, p * 25.0));
          horizontalText.style.opacity = String(Math.max(0, 1 - hProgress));
          horizontalText.style.transform = `translateY(${hProgress * -3500}px)`;
        }

        if (scrollIndicator) {
          scrollIndicator.style.opacity = String(ph(5) ? Math.max(0, 1 - p * 3) : 0);
        }

        const circleIn = Math.min(1, Math.max(0, (p - 0.10) / 0.28));
        const circleOut = Math.min(1, Math.max(0, (p - 0.82) / 0.18));
        const circleScale = circleIn * (1 - circleOut);
        const outerScale = Math.min(1, Math.max(0, (p - 0.18) / 0.24)) * (1 - circleOut);

        if (circleLayer) {
          circleLayer.style.opacity = circleScale > 0.02 ? "1" : "0";
        }

        const mainCircle = sticky.querySelector(".main-circle") as HTMLElement | null;
        if (mainCircle) {
          mainCircle.style.transform = `scale(${circleScale})`;
          mainCircle.style.opacity = String(circleScale);
        }

        const arc1 = sticky.querySelector(".outer-arc-1") as SVGElement | null;
        const arc2 = sticky.querySelector(".outer-arc-2") as SVGElement | null;
        const arc3 = sticky.querySelector(".outer-arc-3") as SVGElement | null;
        if (arc1) {
          arc1.style.opacity = String(outerScale * 0.85 * (1 - circleOut));
          arc1.style.transform = `scale(${outerScale}) rotate(${p * 540}deg)`;
        }
        if (arc2) {
          arc2.style.opacity = String(outerScale * 0.5 * (1 - circleOut));
          arc2.style.transform = `scale(${outerScale}) rotate(${-p * 720}deg)`;
        }
        if (arc3) {
          arc3.style.opacity = String(outerScale * 0.35 * (1 - circleOut));
          arc3.style.transform = `scale(${outerScale}) rotate(${p * 300 + 45}deg)`;
        }

        const textIn = Math.min(1, Math.max(0, (p - 0.40) / 0.14));
        const textOut = Math.min(1, Math.max(0, (p - 0.82) / 0.14));
        const circleTextOpacity = textIn * (1 - textOut) * Math.min(1, Math.max(0, (circleIn - 0.9) / 0.1));
        const circleTextY = (1 - textIn) * 16 + textOut * -12;
        const centerText = sticky.querySelector(".center-text") as HTMLElement | null;
        if (centerText) {
          centerText.style.opacity = String(circleTextOpacity);
          centerText.style.transform = `translateY(${circleTextY}px) scale(${0.85 + circleScale * 0.15})`;
          centerText.style.pointerEvents = circleTextOpacity > 0.5 ? "auto" : "none";
        }

        sticky.style.background = "#ffffff";

        if (curtain) {
          curtain.style.transform = `translateY(${100 - Math.max(0, (p - 0.96) / 0.04) * 100}%)`;
        }

        ticking = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const ph = (n: number) => phase >= n;

  return (
    <>
      {/* Loading */}
      <div
        className="fixed inset-0 z-[200] flex flex-col items-center justify-center"
        style={{
          background: "#ffffff",
          opacity: loading ? 1 : 0,
          pointerEvents: loading ? "all" : "none",
          transition: "opacity 0.6s ease",
        }}
      >
        <div className="flex flex-col items-center gap-8">
          <span style={{ fontFamily: "'Barlow', sans-serif", fontSize: "clamp(22px, 3vw, 32px)", fontWeight: 700, letterSpacing: "0.2em", color: "#1a1a1a" }}>
            CRUPLIX
          </span>
          <div className="relative" style={{ width: "200px", height: "1px", background: "rgba(0,0,0,0.1)" }}>
            <div
              className="absolute left-0 top-0 h-full"
              style={{ background: "#c00000", width: `${loadPct}%`, transition: "width 0.025s linear" }}
            />
          </div>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", letterSpacing: "0.3em", color: "#111111" }}>
            LOADING
          </span>
        </div>
      </div>

      {/* Scroll wrapper */}
      <div ref={wrapRef} style={{ height: "520vh", position: "relative", background: "#ffffff" }}>
        <div
          className="sticky-hero-viewport sticky top-0 overflow-hidden"
          style={{ height: "100vh", background: "#ffffff" }}
        >
          {/* City SVG background */}
          <div
            ref={cityRef}
            className="absolute z-[1] pointer-events-none"
            style={{ opacity: 1, left: 0, right: 0, bottom: 0, top: 0 }}
          >
            <div className="city-svg-wrap absolute w-full" style={{ maxWidth: "2000px", left: "50%", transform: "translateX(-50%) translateY(-22%)", bottom: 0 }}>
              <CitySvg active={!loading} />
            </div>
          </div>

          {/* Diagonal decorative lines */}
          <div className="absolute pointer-events-none hidden lg:block z-[2]" style={{ top: "25%", left: "-15%", width: "50%", height: "1px", background: "rgba(0,0,0,0.04)", transform: "rotate(15deg)", transformOrigin: "left center" }} />
          <div className="absolute pointer-events-none hidden lg:block z-[2]" style={{ top: "65%", right: "-10%", width: "45%", height: "1px", background: "rgba(0,0,0,0.03)", transform: "rotate(-10deg)", transformOrigin: "right center" }} />

          {/* Vertical CRUPLIX text - right margin */}
          <div
            className="hero-vertical-text absolute z-[3] pointer-events-none hidden lg:block overflow-hidden"
            style={{ right: "0px", top: "0", bottom: "0", width: "220px" }}
          >
            <div
              className="hero-vertical-text-track"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "200%",
                animation: "heroVerticalFlow 20s linear infinite",
              }}
            >
              <div style={{ height: "50%", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{
                  writingMode: "vertical-rl",
                  textOrientation: "mixed",
                  fontFamily: "'Barlow', sans-serif",
                  fontSize: "clamp(160px, 22vh, 300px)",
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  color: "rgba(0,0,0,0.04)",
                  lineHeight: 1,
                  userSelect: "none",
                  whiteSpace: "nowrap",
                }}>
                  CRUPLIX
                </span>
              </div>
              <div style={{ height: "50%", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{
                  writingMode: "vertical-rl",
                  textOrientation: "mixed",
                  fontFamily: "'Barlow', sans-serif",
                  fontSize: "clamp(160px, 22vh, 300px)",
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  color: "rgba(0,0,0,0.04)",
                  lineHeight: 1,
                  userSelect: "none",
                  whiteSpace: "nowrap",
                }}>
                  CRUPLIX
                </span>
              </div>
            </div>
          </div>

          {/* Grid lines — building silhouette heights */}
          {[
            { pct: 7,  top: "18%", dots: [20, 35, 55, 70, 82] },
            { pct: 14, top: "5%",  dots: [15, 28, 42, 58, 68, 80] },
            { pct: 21, top: "28%", dots: [25, 45, 65, 80] },
            { pct: 29, top: "10%", dots: [18, 32, 50, 62, 75, 88] },
            { pct: 36, top: "22%", dots: [22, 40, 60, 78] },
            { pct: 43, top: "2%",  dots: [12, 25, 38, 52, 64, 76, 88] },
            { pct: 50, top: "14%", dots: [20, 38, 55, 70, 84] },
            { pct: 57, top: "8%",  dots: [16, 30, 46, 60, 72, 85] },
            { pct: 64, top: "30%", dots: [28, 48, 68, 85] },
            { pct: 71, top: "12%", dots: [18, 34, 50, 65, 78, 90] },
            { pct: 79, top: "20%", dots: [24, 42, 62, 80] },
            { pct: 86, top: "6%",  dots: [14, 28, 44, 58, 72, 86] },
          ].map(({ pct, top, dots }, i) => (
            <div
              key={i}
              className="grid-line absolute bottom-0 z-[2] pointer-events-none hidden md:block"
              style={{
                left: `${pct}%`,
                top,
                width: "1px",
                background: "rgba(0,0,0,0.06)",
                transform: "scaleY(0)",
                transformOrigin: "top center",
                transition: "none",
                opacity: 1,
              }}
            >
              {/* 窓ドット */}
              {dots.map((dotPct, di) => (
                <div
                  key={di}
                  className="absolute"
                  style={{
                    top: `${dotPct}%`,
                    left: "-2px",
                    width: "5px",
                    height: "3px",
                    background: "rgba(0,0,0,0.09)",
                    opacity: buildingVisible ? 1 : 0,
                    transition: buildingVisible ? `opacity 0.4s ease ${i * 0.18 + 6}s` : "none",
                  }}
                />
              ))}
            </div>
          ))}

          {/* Hero content layer */}
          <div
            className="hero-content-layer absolute inset-0 z-[10] flex flex-col justify-center px-5 sm:px-8 md:px-16 lg:px-24 pt-8 md:pt-12 pb-16 md:pb-28"
            style={{
              opacity: 1,
              transform: "translateY(0px)",
              willChange: "opacity, transform",
              pointerEvents: "auto",
            }}
          >
            {/* Main title — top */}
            <h1 className="mb-6 md:mb-10 break-words md:break-keep" style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "clamp(22px, 5vw, 64px)", fontWeight: 700, lineHeight: 1.3, letterSpacing: "0.02em", color: "#1a1a1a", overflowWrap: "break-word" }}>
              <span style={{ display: "block" }}>
                <span className={`hero-title-line${titleVisible ? " in" : ""}`}>
                  あなたの<span className="accent-char" style={{ color: "#c00000" }}>ビジネス</span>の隣に立つ
                </span>
              </span>
              <span style={{ display: "block" }}>
                <span className={`hero-title-line${titleVisible ? " in-delay" : ""}`}>
                  共に前へ
                </span>
              </span>
            </h1>

            {/* Tagline */}
            <div style={{ overflow: "hidden", marginBottom: "10px" }}>
              <p className="text-[11px] md:text-[13px]" style={{ fontFamily: "'Barlow', sans-serif", letterSpacing: "0.45em", color: "#c00000", transform: ph(2) ? "translateY(0)" : "translateY(110%)", transition: "transform 0.8s cubic-bezier(0.22,1,0.36,1)" }}>
                DIGITAL MARKETING AGENCY
              </p>
            </div>

            {/* Top ruled line */}
            <div style={{ height: "1px", background: "rgba(0,0,0,0.08)", width: "min(280px, 60vw)", transform: ph(2) ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left center", transition: "transform 0.75s cubic-bezier(0.22,1,0.36,1)", marginBottom: "24px" }} />

            {/* Company description */}
            <div className="mb-6 md:mb-10" style={{ overflow: "hidden" }}>
              <div style={{ overflow: "hidden" }}>
                <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "clamp(8px, 1vw, 16px)", color: "#555555", lineHeight: 1.8, letterSpacing: "0.02em", transform: ph(3) ? "translateY(0)" : "translateY(110%)", transition: "transform 0.85s cubic-bezier(0.22,1,0.36,1) 0.2s" }}>
                  株式会社クラプリックスは、Web制作・SEO・MEO・LLMO・広告運用・アプリ開発など、
                  <br />
                  デジタルマーケティング全般を一気通貫でサポートする会社です。
                </p>
              </div>
              <div style={{ overflow: "hidden", marginTop: "16px" }}>
                <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "clamp(8px, 1vw, 16px)", color: "#555555", lineHeight: 1.8, letterSpacing: "0.02em", transform: ph(3) ? "translateY(0)" : "translateY(110%)", transition: "transform 0.85s cubic-bezier(0.22,1,0.36,1) 0.4s" }}>
                  華やかなスタートなんてありません。
                  <br />
                  お客様のビジネスの可能性を最大化するため、あらゆるデジタル戦略で、今いる場所から成果へ繋げます。
                </p>
              </div>
            </div>

            {/* Bottom ruled line */}
            <div style={{ height: "1px", background: "rgba(0,0,0,0.08)", width: "min(280px, 60vw)", transform: ph(2) ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left center", transition: "transform 0.75s cubic-bezier(0.22,1,0.36,1) 0.1s", marginBottom: "20px" }} className="md:!mb-10" />

            {/* English copy — desktop only */}
            <div className="hidden md:block mb-4 md:mb-6" style={{}}>
              <div style={{ height: "1px", background: "rgba(0,0,0,0.06)", width: "300px", transform: ph(4) ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left center", transition: "transform 0.7s cubic-bezier(0.22,1,0.36,1) 0.2s", marginBottom: "14px" }} />
              <div style={{ overflow: "hidden" }}>
                <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: "14px", color: "#111111", letterSpacing: "0.08em", fontWeight: 300, fontStyle: "italic", transform: ph(4) ? "translateY(0)" : "translateY(110%)", transition: "transform 0.85s cubic-bezier(0.22,1,0.36,1) 0.22s" }}>
                  Standing beside your business,
                </p>
              </div>
              <div style={{ overflow: "hidden" }}>
                <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: "14px", color: "#111111", letterSpacing: "0.08em", fontWeight: 300, fontStyle: "italic", transform: ph(4) ? "translateY(0)" : "translateY(110%)", transition: "transform 0.85s cubic-bezier(0.22,1,0.36,1) 0.3s" }}>
                  moving forward together.
                </p>
              </div>
              <div style={{ height: "1px", background: "rgba(0,0,0,0.06)", width: "300px", transform: ph(4) ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left center", transition: "transform 0.7s cubic-bezier(0.22,1,0.36,1) 0.35s", marginTop: "14px" }} />
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 md:gap-3" style={{ opacity: ph(5) ? 1 : 0, transform: ph(5) ? "translateY(0)" : "translateY(16px)", transition: "opacity 0.7s ease, transform 0.7s ease" }}>
              <div ref={magneticRef1} className="magnetic-wrap">
                <button
                  onClick={() => navigate("/contact")}
                  className="px-4 py-2.5 md:px-7 md:py-3.5 text-xs md:text-sm tracking-widest cursor-pointer whitespace-nowrap transition-all duration-300 glow-pulse"
                  style={{ background: "#c00000", color: "#ffffff", fontFamily: "'Barlow', sans-serif", fontWeight: 500, letterSpacing: "0.2em" }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "#a00000"; (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "#c00000"; (e.currentTarget as HTMLButtonElement).style.transform = "translateY(0)"; }}
                >
                  無料相談をする
                </button>
              </div>
              <div ref={magneticRef2} className="magnetic-wrap">
                <button
                  onClick={() => navigate("/service")}
                  className="px-4 py-2.5 md:px-7 md:py-3.5 text-xs md:text-sm tracking-widest cursor-pointer whitespace-nowrap transition-all duration-300"
                  style={{ border: "1px solid rgba(0,0,0,0.15)", color: "#1a1a1a", background: "transparent", fontFamily: "'Barlow', sans-serif", fontWeight: 500, letterSpacing: "0.2em" }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "#c00000"; (e.currentTarget as HTMLButtonElement).style.color = "#c00000"; (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(0,0,0,0.15)"; (e.currentTarget as HTMLButtonElement).style.color = "#1a1a1a"; (e.currentTarget as HTMLButtonElement).style.transform = "translateY(0)"; }}
                >
                  サービスを見る
                </button>
              </div>
            </div>

            {/* Decorative oblique ellipse arc */}
            <div
              className="absolute pointer-events-none z-[5] hidden md:block"
              style={{ left: "2%", bottom: "6%", width: "500px", height: "220px", opacity: 0, transition: "opacity 1s ease 0.5s" }}
            >
              <svg viewBox="0 0 400 200" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%", overflow: "visible" }}>
                <path d="M 40 145 A 185 62 -40 0 1 360 55" stroke="rgba(192, 0, 0, 0.35)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                <circle cx="40" cy="145" r="3" fill="#c00000" opacity="0.5" />
                <circle cx="360" cy="55" r="3" fill="#c00000" opacity="0.5" />
              </svg>
            </div>
          </div>

          {/* Circle reveal layer */}
          <div className="circle-reveal-layer absolute inset-0 z-[9] flex items-center justify-center pointer-events-none" style={{ opacity: 0 }}>
            <div className="main-circle absolute" style={{ width: "clamp(340px, 72vmin, 680px)", height: "clamp(340px, 72vmin, 680px)", borderRadius: "50%", border: "2.5px solid rgba(0,0,0,0.25)", transform: "scale(0)", opacity: 0 }} />

            <svg className="outer-arc-1 absolute" style={{ width: "clamp(430px, 92vmin, 860px)", height: "clamp(430px, 92vmin, 860px)", opacity: 0, transform: "scale(0) rotate(0deg)", transition: "none" }} viewBox="0 0 800 800">
              <path d="M 400 22 A 378 378 0 1 1 22 400" fill="none" stroke="rgba(0,0,0,0.25)" strokeWidth="2" strokeLinecap="round" />
            </svg>

            <svg className="outer-arc-2 absolute" style={{ width: "clamp(470px, 100vmin, 940px)", height: "clamp(470px, 100vmin, 940px)", opacity: 0, transform: "scale(0) rotate(0deg)", transition: "none" }} viewBox="0 0 800 800">
              <path d="M 590 68 A 380 380 0 0 1 778 468" fill="none" stroke="rgba(0,0,0,0.18)" strokeWidth="1.8" strokeLinecap="round" />
            </svg>

            <svg className="outer-arc-3 absolute" style={{ width: "clamp(450px, 96vmin, 900px)", height: "clamp(450px, 96vmin, 900px)", opacity: 0, transform: "scale(0) rotate(45deg)", transition: "none" }} viewBox="0 0 800 800">
              <path d="M 720 160 A 360 200 -40 0 1 160 720" fill="none" stroke="rgba(0,0,0,0.12)" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="12 8" />
            </svg>

            <div className="center-text relative z-10 flex flex-col items-center justify-center text-center px-8" style={{ opacity: 0, transform: "translateY(20px) scale(0.85)", maxWidth: "clamp(280px, 44vw, 540px)", pointerEvents: "none" }}>
              <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: "12px", letterSpacing: "0.45em", color: "#c00000", marginBottom: "24px" }}>OUR PHILOSOPHY</p>
              <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "clamp(12px, 2.5vw, 28px)", fontWeight: 500, color: "#1a1a1a", lineHeight: 2.0, letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
                どんな場所からでも、<strong style={{ color: "#c00000", fontWeight: 700 }}>前に進める</strong>———
              </p>
              <div style={{ width: "28px", height: "1px", background: "#c00000", margin: "24px auto" }} />
              <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: "clamp(13px, 1.5vw, 17px)", color: "#111111", letterSpacing: "0.1em", fontStyle: "italic", lineHeight: 2 }}>
                Forward, from anywhere.
              </p>
            </div>
          </div>

          {/* Scroll Down indicator */}
          <div className="scroll-indicator absolute bottom-4 right-4 md:bottom-6 md:right-12 z-[10] flex flex-col items-center gap-3" style={{ opacity: ph(5) ? 1 : 0, transition: "opacity 0.7s ease 0.3s" }}>
            <div className="flex flex-col gap-1.5 items-center">
              <span className="scroll-dot w-1 h-1 rounded-full block" style={{ background: "#c00000" }} />
              <span className="scroll-dot w-1 h-1 rounded-full block" style={{ background: "#c00000" }} />
              <span className="scroll-dot w-1 h-1 rounded-full block" style={{ background: "#c00000" }} />
            </div>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", letterSpacing: "0.3em", color: "#111111", writingMode: "vertical-rl" }}>
              Scroll Down
            </span>
          </div>

          {/* Bottom label */}
          <div
            className="absolute bottom-6 md:bottom-10 left-4 md:left-12 lg:left-20 flex items-center gap-3"
            style={{ zIndex: 30 }}
          >
            <span
              className="text-xs tracking-[0.3em]"
              style={{
                fontFamily: "'Barlow', sans-serif",
                color: 'rgba(255,255,255,0.5)',
              }}
            >
              SCROLL DOWN
            </span>
            <i className="ri-arrow-down-line text-sm" style={{ color: 'rgba(255,255,255,0.4)' }} />
          </div>

          {/* Right vertical accent line */}
          <div className="right-accent-line absolute right-5 md:right-12 top-0 bottom-0 z-[3] pointer-events-none" style={{ width: "1px", background: "rgba(0,0,0,0.08)", transform: ph(1) ? "scaleY(1)" : "scaleY(0)", transformOrigin: "top center", transition: "transform 1.2s cubic-bezier(0.22,1,0.36,1) 0.3s", opacity: 1 }} />

          {/* Bottom transition curtain */}
          <div className="bottom-curtain absolute bottom-0 left-0 right-0 z-[20] pointer-events-none" style={{ height: "100%", background: "#ffffff", transform: "translateY(100%)", boxShadow: "none", willChange: "transform" }} />
        </div>
      </div>

      {/* Mobile-only: scale up city buildings */}
      <style>{`
        @media (max-width: 767px) {
          .city-svg-wrap {
            transform: translateX(-50%) translateY(-18%) scale(1.45) !important;
          }
        }
      `}</style>
    </>
  );
}